import type { CheckoutSdk } from '@athena-lib/Checkout/utils';
import {
  createCheckoutInstance,
  type CartCommandResponse,
  type CountriesResponse,
} from '@stickerapp-org/checkout-sdk';
import type { TranslationPairs } from '@athena-lib/ProductWizard/types';
import { Type } from '@sinclair/typebox';
import type { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
import { setGlobalError } from '../components/GlobalSdkError.svelte';
import type { ExpandedCheckoutSettings } from './server/expanders/checkoutSettingsExpander';
import { post } from './http';
import { loadCheckoutWithDefaults } from './utilities/checkout';
import { getEnhancedCommereceEventsTracker } from '$lib/gtmEventHelper.svelte.js';
import { goto } from '$app/navigation';

export const checkoutIdCookieKey = 'checkout-cart-id';
export const checkoutVersionCookieKey = 'checkout-cart-version';

let checkout = $state<{
  sdk: null | CheckoutSdk;
  settings?: ExpandedCheckoutSettings;
  suggestedPostalCode?: string;
  countries?: CountriesResponse;
  translators?: {
    translateLaminate: (product: string) => string;
    translateMaterial: (material: string) => string;
    translateProductName: (productName: string) => string;
  };
}>({ sdk: null });

/**
  Gets the initialized instance of checkout sdk, should only be @null if checkoutSdk has
  not been initialized.
 */
export const getCheckoutSdk = () => checkout?.sdk;
export const setCheckoutState = (newCheckout: typeof checkout) => {
  checkout = newCheckout;
  const translationPairs = newCheckout.settings?.translationPairs;
  if (translationPairs) {
    checkout.translators = {
      translateMaterial: createMaterialTranslator(translationPairs),
      translateLaminate: createLaminateTranslator(translationPairs),
      translateProductName: createProductNameTranslator(translationPairs),
    };
  }

  let countriesResult: CountriesResponse | undefined;
  newCheckout.sdk?.countries().then((x) => {
    countriesResult = x;
  });
  checkout = {
    ...checkout,
    get countries() {
      return countriesResult;
    },
  };
};

export const getCheckoutCountries = () => checkout.countries;

export const createMaterialTranslator =
  (translationPairs: TranslationPairs) => (material: string) => {
    const key = `products_material_${material}`;
    return translationPairs?.[key] ?? key;
  };

export const createLaminateTranslator =
  (translationPairs: TranslationPairs) => (laminate: string) => {
    const key = `products_laminate_${laminate}`;
    return translationPairs?.[key] ?? key;
  };

export const createProductNameTranslator =
  (translationPairs: TranslationPairs) => (productName: string) => {
    const key = `products_product_name_${productName}`;
    return translationPairs?.[key] ?? key;
  };

export const getProductsTranslators = () => checkout.translators;

export const getSuggestedPostalCode = () => {
  const sdkPostCode = checkout.sdk?.state.customer?.address?.postalCode;
  return sdkPostCode || checkout.suggestedPostalCode;
};

export const setSuggestedPostalCode = (postalCode: string) => {
  checkout = { ...checkout, suggestedPostalCode: postalCode };
};

export const getCheckoutSettings = () => checkout.settings;

const saveUserCountry = (countryCode: string) => {
  return post('/api/set_user_country', { countryCode }, Type.Unknown());
};

export const initializeCheckoutSdk = ({
  siteUrl,
  orderConfirmationPageUrl,
  checkoutApiBaseUrl,
  checkoutPageUrl,
  userCountryCode,
  currency,
  language,
  jwt,
  atlasSessionId,
  translations,
}: {
  checkoutPageUrl: string;
  checkoutApiBaseUrl: string;
  orderConfirmationPageUrl: string;
  userCountryCode: string;
  currency: string;
  language: string;
  siteUrl: string;
  jwt: string;
  atlasSessionId: string | undefined;
  translations: ReturnType<typeof getCommonTranslations>;
}) => {
  const cartId = localStorage.getItem(checkoutIdCookieKey) ?? undefined;
  const cartVersion = localStorage.getItem(checkoutVersionCookieKey) ?? undefined;
  let countryCode = userCountryCode;

  const cartInstance = createCheckoutInstance({
    cartId,
    cartVersion,
    checkoutApiBaseUrl,
    countryCode: countryCode.toLowerCase(),
    currency,
    language,
    siteUrl,
    checkoutPageUrl,
    orderConfirmationPageUrl,
    tokenString: jwt,
    onShowOrderConfirmation: (orderId) => {
      return goto(`${orderConfirmationPageUrl}?orderId=${encodeURIComponent(orderId)}`);
    },
    onUnexpectedError: setGlobalError,
    atlasSessionId: atlasSessionId ?? '',
    translateApiMessage: (errorCode) => {
      const translationKey = errorCode ? apiErrorToTranslationsMap[errorCode] : undefined;
      if (translationKey) {
        return translations[translationKey];
      }
      return undefined;
    },
  });

  const cartEventsTracker = getEnhancedCommereceEventsTracker();

  const oldCompletePurchase = cartInstance.actions.completePurchase;
  cartInstance.actions.completePurchase = async (...args) => {
    const onPurchase = await cartEventsTracker.purchase().catch(() => (_orderId?: string) => {});
    const result = await oldCompletePurchase(...args);
    const id = 'orderId' in result ? result.orderId : undefined;
    onPurchase(id);
    return result;
  };

  const oldUpdateCountry = cartInstance.updateCountry;
  cartInstance.updateCountry = async (...args) => {
    const result = await oldUpdateCountry(...args);
    await loadCheckoutWithDefaults();
    return result;
  };

  cartInstance.state.onChange((newState) => {
    if (countryCode !== newState.countryCode) {
      countryCode = newState.countryCode;
      saveUserCountry(countryCode);
    }
    cartEventsTracker.reportUpdatedLineItems(newState);
    Object.assign(checkout.sdk ?? cartInstance, cartInstance);

    localStorage.setItem(checkoutIdCookieKey, newState.id);
    localStorage.setItem(checkoutVersionCookieKey, newState.version);
  });
  return cartInstance;
};

type GetCustomSticker = CheckoutSdk['getCustomSticker'];

export const getCustomSticker = (id: string, version?: string): ReturnType<GetCustomSticker> => {
  const sdk = getCheckoutSdk();
  if (!sdk) {
    return Promise.reject('No SDK initialized');
  }
  return sdk.getCustomSticker(id, version);
};

type CartCommandError = Exclude<CartCommandResponse['errorCode'], undefined>;

const apiErrorToTranslationsMap: Record<
  CartCommandError,
  keyof ReturnType<typeof getCommonTranslations> | undefined
> = {
  CANNOT_BUY_GIFT_CARDS_WITH_A_GIFT_CARD: 'giftCardCannotBeUsedToBuyGiftCard',
  CART_HAS_ALREADY_BEEN_PAID_FOR: 'cartHasAlreadyBeenPaidFor',
  UNKNOWN_ERROR: 'anErrorHasOccured',
  INVALID_SHIPPING_METHOD: 'invalidShippingMethod',
  STICKER_PACK_NOT_FOUND: 'stickerPackNotFound',
  LINE_ITEM_NOT_FOUND: 'lineItemNotFound',
  ORDER_NOT_FOUND: 'orderNotFound',
  CODE_ALREADY_ADDED: 'codeAlreadyAdded',
  INVALID_CODE: 'invalidCode',
  GIFT_CARD_ALREADY_USED: 'giftCardAlreadyUsed',
  CODE_NOT_FOUND: 'codeNotFound',
  INVALID_VAT_NUMBER: 'invalidVatNumber',
  CATALOG_ITEM_NOT_FOUND: 'catalogItemNotFound',
  CATALOG_ITEM_ALREADY_IN_ANOTHER_CART: 'catalogItemAlreadyInAnotherCart',
  QUANTITY_MUST_BE_GREATER_THAN_0: 'quantityMustBeGreaterThan0',
  SHOP_ITEM_NOT_FOUND: 'shopItemNotFound',
  CUSTOM_STICKER_NOT_FOUND: 'customStickerNotFound',
  INVALID_PAYMENT_METHOD: 'invalidPaymentMethod',
  INVALID_EMAIL: 'invalidEmail',
};
